// eslint-disable-next-line no-restricted-imports
import {
  DefaultApi as AccountApi,
  Configuration,
  DefaultApiGetAvailableCurrencyRequest,
  DefaultApiGetCurrencyBalanceRequest,
  DefaultApiGetHistoricalAccountsRequest,
  DefaultApiGetHistoricalBalancesRequest,
  DefaultApiGetSourceCurrenciesRequest,
  DefaultApiGetSourceCurrenciesWithBalanceRequest,
  DefaultApiGetSourceCurrenciesWithTdayBalanceRequest,
  DefaultApiGetTargetCurrenciesRequest,
  GetAvailableCurrency200Response,
  GetHistoricalBalancesResponse,
  GetHistoricalBalancesResponseDataPointsInner,
  SourceCurrency,
  SourceCurrencyWithBalance,
  SourceCurrencyWithTdayBalance,
} from '@eih-netbroker/api-account';
import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { IConfigAPI } from '../config';
import { ResourceHeadersWithToken } from '../services';
import { BaseResource } from './base.resource';
import { resolveResourceBasePath } from './resourceBasePath';

export type SourceCurrenciesRequest = Omit<DefaultApiGetSourceCurrenciesRequest, keyof ResourceHeadersWithToken>;
export type TargetCurrenciesRequest = Omit<DefaultApiGetTargetCurrenciesRequest, keyof ResourceHeadersWithToken>;
export type SourceCurrenciesWithBalanceRequest = Omit<
  DefaultApiGetSourceCurrenciesWithBalanceRequest,
  keyof ResourceHeadersWithToken
>;
export type SourceCurrenciesWithTdayBalanceRequest = Omit<
  DefaultApiGetSourceCurrenciesWithTdayBalanceRequest,
  keyof ResourceHeadersWithToken
>;
export type CurrencyBalanceRequest = Omit<DefaultApiGetCurrencyBalanceRequest, keyof ResourceHeadersWithToken>;
export type CurrencyBalanceForOrderRequest = Omit<
  DefaultApiGetAvailableCurrencyRequest,
  keyof ResourceHeadersWithToken
>;
export type HistoricalBalancesRequest = Omit<DefaultApiGetHistoricalBalancesRequest, keyof ResourceHeadersWithToken>;
export type HistoricalAccountsRequest = Omit<DefaultApiGetHistoricalAccountsRequest, keyof ResourceHeadersWithToken>;

export type OrderCurrencyExchangeSourceCurrency = SourceCurrency;
export type OrderCurrencyExchangeBalance = GetAvailableCurrency200Response;
export type OrderCurrencySourceCurrencyWithBalance = SourceCurrencyWithBalance;
export type OrderCurrencySourceCurrencyWithTdayBalance = SourceCurrencyWithTdayBalance;

export type HistoricalBalanceRecord = GetHistoricalBalancesResponse;
export type HistoricalBalanceRecordDataPointsInner = GetHistoricalBalancesResponseDataPointsInner;

export class AccountResource extends BaseResource {
  // Name has to be overridden, since the build job renames it to 'r' and
  // every resource class will have the same name
  // @ts-ignore
  static readonly name = 'AccountResource';
  private accountApi: AccountApi;

  constructor(axiosInstance: AxiosInstance, basePath: string, configApi: IConfigAPI) {
    const accountBasePath = resolveResourceBasePath(basePath, configApi.usUrl.account);
    super(axiosInstance, accountBasePath, configApi);
    this.accountApi = new AccountApi(
      new Configuration({
        basePath: accountBasePath,
      }),
      accountBasePath,
      axiosInstance,
    );
  }

  async getSourceCurrencies(
    request: SourceCurrenciesRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<Array<OrderCurrencyExchangeSourceCurrency>>> {
    return this.accountApi.getSourceCurrencies({ ...request, ...headers });
  }

  async getTargetCurrencies(request: TargetCurrenciesRequest, headers: ResourceHeadersWithToken) {
    return this.accountApi.getTargetCurrencies({ ...request, ...headers });
  }

  async getCurrencyBalance(request: CurrencyBalanceRequest, headers: ResourceHeadersWithToken) {
    return this.accountApi.getCurrencyBalance({ ...request, ...headers });
  }

  async getSourceCurrenciesWithBalance(request: SourceCurrenciesWithBalanceRequest, headers: ResourceHeadersWithToken) {
    return this.accountApi.getSourceCurrenciesWithBalance({ ...request, ...headers });
  }

  async getSourceCurrenciesWithTdayBalance(
    request: SourceCurrenciesWithTdayBalanceRequest,
    headers: ResourceHeadersWithToken,
  ) {
    return this.accountApi.getSourceCurrenciesWithTdayBalance({ ...request, ...headers });
  }

  async getCurrencyBalanceForOrder(
    request: CurrencyBalanceForOrderRequest,
    headers: ResourceHeadersWithToken,
  ): Promise<AxiosResponse<OrderCurrencyExchangeBalance>> {
    return this.accountApi.getAvailableCurrency({ ...request, ...headers });
  }

  async getHistoricalBalances(
    request: HistoricalBalancesRequest,
    headers: ResourceHeadersWithToken,
    options?: AxiosRequestConfig,
  ) {
    return this.accountApi.getHistoricalBalances({ ...request, ...headers }, options);
  }

  async getHistoricalBalancesByClient(
    request: HistoricalBalancesRequest,
    headers: ResourceHeadersWithToken,
    options?: AxiosRequestConfig,
  ) {
    return this.accountApi.getHistoricalBalances({ ...request, ...headers }, options);
  }

  async getHistoricalAccounts(request: HistoricalAccountsRequest, headers: ResourceHeadersWithToken) {
    return this.accountApi.getHistoricalAccounts({ ...request, ...headers });
  }
}
